import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Fuel,
  UtensilsCrossed,
  Building2,
  ShieldCheck,
  Bot,
  Presentation,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  Sparkles,
  Zap,
  Activity,
  Layers,
  Database,
  Smartphone,
  Mail,
  FileSpreadsheet,
  AlertTriangle,
} from 'lucide-react';

export const Solutions: React.FC = () => {
  return (
    <div style={{ paddingTop: '80px' }}>
      {/* =========================================================================
          HERO SECTION
          ========================================================================= */}
      <section
        style={{
          paddingTop: '64px',
          paddingBottom: '80px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'radial-gradient(ellipse 70% 50% at 50% 0%, var(--brand-red-glow-subtle), transparent)',
        }}
      >
        <div className="container-max" style={{ textAlign: 'center', maxWidth: '880px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span className="badge-pill badge-red">
              <Sparkles size={12} />
              INDUSTRY-SPECIFIC FINANCIAL INTELLIGENCE
            </span>
          </div>

          <h1
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              lineHeight: '1.1',
              marginBottom: '20px',
            }}
          >
            Built for Your Industry.{' '}
            <br />
            <span
              className="font-serif"
              style={{
                color: 'var(--brand-red)',
                fontStyle: 'italic',
                fontWeight: 400,
              }}
            >
              Tailored to Your Operations.
            </span>
          </h1>

          <p
            style={{
              fontSize: '1.15rem',
              color: 'var(--text-secondary)',
              lineHeight: '1.65',
              maxWidth: '740px',
              margin: '0 auto 32px auto',
            }}
          >
            Generic accounting software forces complex operational businesses into rigid boxes.
            Forty2 embeds directly into your field operations — from petrol pumps and cloud kitchens
            to real estate projects and statutory tax audits.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <Link to="/diagnostic" className="btn-primary" style={{ padding: '14px 28px', fontSize: '1rem' }}>
              <span>Take the 12-Point Diagnostic</span>
              <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn-secondary" style={{ padding: '14px 24px', fontSize: '1rem' }}>
              <span>Book an Operational Demo</span>
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 1: FUEL RETAIL & PETROL PUMPS
          ========================================================================= */}
      <section style={{ padding: '96px 0', borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="container-max">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: '48px',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <span className="badge-pill badge-red font-mono">
                  <Fuel size={14} />
                  PETROLEUM VERTICAL
                </span>
              </div>
              <h2
                style={{
                  fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  lineHeight: '1.2',
                  marginBottom: '18px',
                  letterSpacing: '-0.02em',
                }}
              >
                Nozzle-to-Ledger Intelligence for Fuel Stations
              </h2>
              <p
                style={{
                  fontSize: '1.05rem',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.65',
                  marginBottom: '24px',
                }}
              >
                Stop losing sleep over shift handovers and tank calibration discrepancies.
                Forty2 connects directly with <strong style={{ color: 'var(--text-primary)' }}>BPCL IoTHOS</strong> and automated Multi-Product Dispenser (MPD) controllers, replacing physical shift registers with real-time auditability.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '32px' }}>
                {[
                  {
                    title: 'Automated MPD Meter Synchronization',
                    desc: 'Real-time nozzle meter readings ingested without manual human intervention.',
                  },
                  {
                    title: '4-Way Shift Handover Balancing',
                    desc: 'Instant cross-verification of meter sales against physical tank dip, cash in hand, PineLabs card swipes, and fleet credit slips.',
                  },
                  {
                    title: 'Density & Evaporation Loss Tracking',
                    desc: 'Flags temperature-induced shrinkage and calibration drift before it drains margin.',
                  },
                  {
                    title: 'Fleet Credit Khata & WhatsApp Statements',
                    desc: 'Automated periodic invoicing and WhatsApp Statement of Accounts for transport fleet customers.',
                  },
                ].map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '12px' }}>
                    <CheckCircle2 size={18} color="var(--brand-red)" style={{ flexShrink: 0, marginTop: '3px' }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual Card */}
            <div
              className="glass-card-interactive"
              style={{
                padding: '36px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '16px',
                border: '1px solid var(--border-medium)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <span className="font-mono" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  PUMP AUTOMATION TELEMETRY
                </span>
                <span
                  style={{
                    backgroundColor: 'rgba(0, 230, 118, 0.15)',
                    color: 'var(--signal-green)',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    padding: '4px 8px',
                    borderRadius: '6px',
                  }}
                >
                  LIVE SYNC ACTIVE
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ padding: '16px', backgroundColor: 'var(--bg-surface)', borderRadius: '10px' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Motor Spirit (Petrol) Shift Sales</div>
                  <div className="font-mono" style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '4px' }}>
                    4,820.40 Litres <span style={{ fontSize: '0.85rem', color: 'var(--signal-green)', fontWeight: 600 }}>• Dip Variance +0.02%</span>
                  </div>
                </div>

                <div style={{ padding: '16px', backgroundColor: 'var(--bg-surface)', borderRadius: '10px' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>High Speed Diesel (HSD) Shift Sales</div>
                  <div className="font-mono" style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '4px' }}>
                    9,140.80 Litres <span style={{ fontSize: '0.85rem', color: 'var(--signal-green)', fontWeight: 600 }}>• Dip Variance -0.01%</span>
                  </div>
                </div>

                <div style={{ padding: '16px', backgroundColor: 'var(--bg-surface)', borderRadius: '10px' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Payment Settlement Breakdown</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Cash Drawer:</span>
                      <div className="font-mono" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>₹ 4,52,300</div>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>PineLabs EDC / UPI:</span>
                      <div className="font-mono" style={{ fontWeight: 700, color: 'var(--signal-green)' }}>₹ 8,42,500</div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-subtle)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                ✓ Generated corresponding sales vouchers in Tally Daybook automatically.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 2: F&B & RESTAURANT RECONCILIATION
          ========================================================================= */}
      <section style={{ padding: '96px 0', borderBottom: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-surface)' }}>
        <div className="container-max">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: '48px',
              alignItems: 'center',
            }}
          >
            {/* Visual Card on Left */}
            <div
              className="glass-card-interactive"
              style={{
                padding: '36px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '16px',
                border: '1px solid var(--border-medium)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <span className="font-mono" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  3-WAY SETTLEMENT RADAR
                </span>
                <span
                  style={{
                    backgroundColor: 'rgba(255, 179, 0, 0.15)',
                    color: 'var(--signal-amber)',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    padding: '4px 8px',
                    borderRadius: '6px',
                  }}
                >
                  DISCREPANCIES FLAGGED
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ padding: '16px', backgroundColor: 'var(--bg-deep)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>Swiggy Payout Cycle #41</span>
                    <span style={{ color: 'var(--signal-green)', fontSize: '0.8rem', fontWeight: 600 }}>100% Matched</span>
                  </div>
                  <div className="font-mono" style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '6px' }}>
                    ₹ 1,84,120.00
                  </div>
                </div>

                <div style={{ padding: '16px', backgroundColor: 'var(--bg-deep)', borderRadius: '10px', border: '1px solid var(--border-red)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>Zomato Payout Cycle #82</span>
                    <span style={{ color: 'var(--brand-red-light)', fontSize: '0.8rem', fontWeight: 600 }}>₹ 6,420 Dispute</span>
                  </div>
                  <div className="font-mono" style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--brand-red-light)', marginTop: '6px' }}>
                    ₹ 1,22,800.00 <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>(Unverified Cancellation Fee)</span>
                  </div>
                </div>

                <div style={{ padding: '16px', backgroundColor: 'var(--bg-deep)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>PineLabs EDC Terminal Swipes</span>
                    <span style={{ color: 'var(--signal-green)', fontSize: '0.8rem', fontWeight: 600 }}>Settled to HDFC</span>
                  </div>
                  <div className="font-mono" style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '6px' }}>
                    ₹ 94,800.00
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-subtle)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                ✓ Export dispute packet formatted with order IDs for rapid aggregator reimbursement.
              </div>
            </div>

            {/* Content on Right */}
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <span className="badge-pill badge-red font-mono">
                  <UtensilsCrossed size={14} />
                  F&B & HOSPITALITY VERTICAL
                </span>
              </div>
              <h2
                style={{
                  fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  lineHeight: '1.2',
                  marginBottom: '18px',
                  letterSpacing: '-0.02em',
                }}
              >
                Eliminate Aggregator Deductions & Card Settlement Leakage
              </h2>
              <p
                style={{
                  fontSize: '1.05rem',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.65',
                  marginBottom: '24px',
                }}
              >
                For high-volume restaurants and multi-city cloud kitchens, food delivery aggregators and card machines deduct arbitrary fees, cancellation penalties, and delayed payouts. Forty2 runs continuous 3-way reconciliation to guarantee every single rupee reaches your bank account.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '32px' }}>
                {[
                  {
                    title: 'Automated 3-Way Reconciliation',
                    desc: 'Cross-checks POS KOT orders against Swiggy/Zomato settlement reports and actual bank credits.',
                  },
                  {
                    title: 'PineLabs EDC Batch Reconciliation',
                    desc: 'Validates EDC terminal batch settlements against bank deposits, highlighting uncredited batches and MDR charges.',
                  },
                  {
                    title: 'Dispute Packet Generation',
                    desc: 'Assembles order IDs, timestamps, and amounts for immediate deduction dispute recovery.',
                  },
                  {
                    title: 'Recipe & Food Cost (COGS) Auditing',
                    desc: 'Monitors raw material consumption against billed dishes to spot kitchen wastage and pilferage.',
                  },
                ].map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '12px' }}>
                    <CheckCircle2 size={18} color="var(--brand-red)" style={{ flexShrink: 0, marginTop: '3px' }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 3: STATUTORY TAX AUDIT & COMPLIANCE SENTINEL
          ========================================================================= */}
      <section style={{ padding: '96px 0', borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="container-max">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: '48px',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <span className="badge-pill badge-red font-mono">
                  <ShieldCheck size={14} />
                  CA ASSURANCE & TAX SHIELD
                </span>
              </div>
              <h2
                style={{
                  fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  lineHeight: '1.2',
                  marginBottom: '18px',
                  letterSpacing: '-0.02em',
                }}
              >
                Automated Form 3CD & MSME Section 43B(h) Sentinel
              </h2>
              <p
                style={{
                  fontSize: '1.05rem',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.65',
                  marginBottom: '24px',
                }}
              >
                Statutory audit compliance should not be an emergency drill in September. Forty2’s compliance engine continuously scans every transaction against the Income Tax Act, GST rules, and MSME guidelines.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '32px' }}>
                {[
                  {
                    title: 'Section 43B(h) MSME Countdown Timer',
                    desc: 'Live alerts for overdue vendor balances approaching the 15-day or 45-day threshold to avoid expense disallowance and taxation.',
                  },
                  {
                    title: 'Section 40A(3) Cash Limit Guard',
                    desc: 'Instant flags for cash payments exceeding ₹10,000 per person per day before vouchers are finalized.',
                  },
                  {
                    title: 'GST 2B vs Purchase Auto-Matching',
                    desc: 'Identifies missing vendor invoices and uncredited Input Tax Credit (ITC) before monthly GSTR-3B filing.',
                  },
                  {
                    title: 'One-Click Tax Audit Working Papers',
                    desc: 'Generates Form 3CD clauses with drill-down sampling for external Chartered Accountants.',
                  },
                ].map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '12px' }}>
                    <CheckCircle2 size={18} color="var(--brand-red)" style={{ flexShrink: 0, marginTop: '3px' }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance UI Mock Card */}
            <div
              className="glass-card-interactive"
              style={{
                padding: '36px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '16px',
                border: '1px solid var(--border-medium)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <span className="font-mono" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  STATUTORY COMPLIANCE MONITOR
                </span>
                <span
                  style={{
                    backgroundColor: 'rgba(229, 57, 53, 0.15)',
                    color: 'var(--brand-red-light)',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    padding: '4px 8px',
                    borderRadius: '6px',
                  }}
                >
                  ACTION REQUIRED
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ padding: '14px', backgroundColor: 'var(--bg-surface)', borderRadius: '8px', borderLeft: '4px solid var(--brand-red)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>MSME 43B(h) Overdue Alert</span>
                    <span className="font-mono" style={{ fontSize: '0.75rem', color: 'var(--brand-red-light)', fontWeight: 700 }}>Due in 3 Days</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    ₹ 1,45,000 payable to Precision Tools Ltd (Registered Micro Enterprise).
                  </div>
                </div>

                <div style={{ padding: '14px', backgroundColor: 'var(--bg-surface)', borderRadius: '8px', borderLeft: '4px solid var(--signal-green)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Section 40A(3) Cash Ceiling</span>
                    <span className="font-mono" style={{ fontSize: '0.75rem', color: 'var(--signal-green)', fontWeight: 700 }}>Clean (0 Violations)</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    All cash voucher disbursements strictly under ₹10,000 threshold.
                  </div>
                </div>

                <div style={{ padding: '14px', backgroundColor: 'var(--bg-surface)', borderRadius: '8px', borderLeft: '4px solid var(--signal-blue)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>GSTR-2B Input Tax Credit</span>
                    <span className="font-mono" style={{ fontSize: '0.75rem', color: 'var(--signal-blue)', fontWeight: 700 }}>₹ 84,200 Unclaimed ITC</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    3 supplier invoices found in 2B portal but missing in local purchase register.
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border-subtle)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                ✓ Export 44AB tax audit checklist directly into MS Excel for your statutory auditor.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 4: AUTONOMOUS INGESTION & BOARD DECISION ENGINE
          ========================================================================= */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--bg-surface)' }}>
        <div className="container-max">
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 60px auto' }}>
            <span className="badge-pill badge-red" style={{ marginBottom: '14px' }}>
              CROSS-INDUSTRY CAPABILITIES
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 3.8vw, 2.85rem)',
                fontWeight: 800,
                color: 'var(--text-primary)',
                lineHeight: '1.18',
                marginBottom: '16px',
                letterSpacing: '-0.02em',
              }}
            >
              Zero Data Entry. One-Click Executive Reporting.
            </h2>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Designed to eliminate clerical backlogs and provide instant clarity to business owners and boards.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '28px',
            }}
          >
            {/* Card 1 */}
            <div
              className="glass-card-interactive"
              style={{
                padding: '36px 30px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '16px',
              }}
            >
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  backgroundColor: 'var(--brand-red-glow)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--brand-red)',
                  marginBottom: '20px',
                }}
              >
                <Smartphone size={22} />
              </div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                WhatsApp & Voice Note Logging
              </h3>
              <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '16px' }}>
                Field executives and directors simply record a quick WhatsApp voice note or snap an invoice photo. Forty2 extracts line items, maps tax ledgers, and queues vouchers for one-click confirmation.
              </p>
              <div style={{ fontSize: '0.82rem', color: 'var(--brand-red-light)', fontWeight: 600 }}>
                ✓ Zero desktop data entry required
              </div>
            </div>

            {/* Card 2 */}
            <div
              className="glass-card-interactive"
              style={{
                padding: '36px 30px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '16px',
              }}
            >
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  backgroundColor: 'var(--brand-red-glow)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--brand-red)',
                  marginBottom: '20px',
                }}
              >
                <Presentation size={22} />
              </div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                1-Click Board PPTX Presentations
              </h3>
              <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '16px' }}>
                Generate presentation-ready PowerPoint (`.pptx`) decks with executive financial ratios, EBITDA walk, cash burn runway, and unit economics directly from live Tally ledgers in seconds.
              </p>
              <div style={{ fontSize: '0.82rem', color: 'var(--brand-red-light)', fontWeight: 600 }}>
                ✓ Saves 15+ hours per month on board MIS
              </div>
            </div>

            {/* Card 3 */}
            <div
              className="glass-card-interactive"
              style={{
                padding: '36px 30px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '16px',
              }}
            >
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  backgroundColor: 'var(--brand-red-glow)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--brand-red)',
                  marginBottom: '20px',
                }}
              >
                <Mail size={22} />
              </div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
                Inbound Email Ingestion
              </h3>
              <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '16px' }}>
                Forward supplier bills and bank notices to your dedicated inbox (`invoices@company.forty2.ai`). PDF tables are parsed, GSTIN verified against the portal, and staged for your accountant.
              </p>
              <div style={{ fontSize: '0.82rem', color: 'var(--brand-red-light)', fontWeight: 600 }}>
                ✓ Automatic supplier invoice extraction
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          BOTTOM CTA
          ========================================================================= */}
      <section style={{ padding: '96px 0' }}>
        <div className="container-max">
          <div
            className="glass-panel-elevated"
            style={{
              padding: '60px 40px',
              textAlign: 'center',
              borderRadius: '20px',
              border: '1px solid var(--border-red)',
            }}
          >
            <span className="badge-pill badge-red" style={{ marginBottom: '16px' }}>
              GET STARTED
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
                fontWeight: 800,
                color: 'var(--text-primary)',
                marginBottom: '16px',
                letterSpacing: '-0.02em',
              }}
            >
              See How Forty2 Fits Your Operations
            </h2>
            <p
              style={{
                fontSize: '1.1rem',
                color: 'var(--text-secondary)',
                maxWidth: '620px',
                margin: '0 auto 32px auto',
                lineHeight: '1.6',
              }}
            >
              Take the 2-minute diagnostic benchmark to see where your business has leakages in reconciliation, tax exposure, or reporting delays.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <Link to="/diagnostic" className="btn-primary" style={{ padding: '16px 36px', fontSize: '1.05rem' }}>
                <span>Launch 12-Point Diagnostic</span>
                <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn-secondary" style={{ padding: '16px 28px', fontSize: '1.05rem' }}>
                <span>Talk to a Specialist</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

